import React, { useState, useEffect, useRef, use } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { CDN_URL, PARKI_WEBSITE } from '../common/constants';
import UserAvatar from './IconAvatar';
import ParkiSettingsDropdown from './ParkiSettingsDropdown';
import auth from '../../utils/auth';
import UserSwitcherIcon from './UserSwitcherIcon';

const Navbar = ({ 
    user = { 
        firstName: 'John', 
        lastName: 'Doe', 
        userType: 'customer', 
        entityName: 'Unknown Entity', 
        entityId: null,
        isEntityAdmin: false,
        userId: null,
        email: null
    }, 
    toggleSidebar,
    selectedReport = null,
    sidebarCollapsed = false
}) => {

    const history = useHistory();

    // Helper function to display user name or email
    const getUserDisplayName = () => {
        if (!user.firstName && !user.lastName) {
            return user.email || "Unknown User";
        }
        return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    };

    const handleLinkClick = (event) => {
        event.preventDefault();
        console.log("Navbar link clicked");
    };
   
    // Use the passed toggle function in the mobile menu button
    const handleMobileMenuClick = (event) => {
        event.preventDefault();
        toggleSidebar();
    };

    // Handle logout
    const handleLogout = async (event) => {
        event.preventDefault();
        
        try {
            await auth.logout(); // Now awaits the async logout function
            history.push('/login');
        } catch (error) {
            console.error('Logout failed:', error);
            // Still redirect to login even if logout API fails
            history.push('/login');
        }
    };

    return (
        <nav
            className="layout-navbar container-xxl navbar-detached navbar navbar-expand-xl align-items-center bg-navbar-theme"
            id="layout-navbar"
            style={{ maxWidth: '1650px' }}
        >
            <div className="layout-menu-toggle navbar-nav align-items-xl-center me-3 me-xl-0 d-xl-none">
                <a className="nav-item nav-link px-0 me-xl-6" href="#" onClick={handleMobileMenuClick}>
                    <i className="icon-base ti tabler-menu-2 icon-md"></i>
                </a>
            </div>

            {/* Report Title Section - Left side of navbar */}
            {selectedReport && (
                <div className="navbar-nav align-items-center flex-grow-1 d-none d-md-flex">
                    <div className="d-flex align-items-center">
                        {selectedReport.iconPath && (
                            <img 
                                src={selectedReport.iconPath} 
                                alt="" 
                                className="me-2" 
                                style={{ height: '28px', width: 'auto' }} 
                            />
                        )}
                        <h5 className="m-0 text-truncate">{selectedReport.reportFriendly}</h5>
                    </div>
                </div>
            )}

            <div className="navbar-nav-right d-flex align-items-center justify-content-end" id="navbar-collapse">
                {/* Mobile Report Title - Only visible on mobile */}
                {selectedReport && (
                    <div className="navbar-nav align-items-center me-auto d-flex d-md-none">
                        <div className="d-flex align-items-center">
                            {selectedReport.iconPath && (
                                <img 
                                    src={selectedReport.iconPath} 
                                    alt="" 
                                    className="me-2" 
                                    style={{ height: '24px', width: 'auto' }} 
                                />
                            )}
                            <h6 className="m-0 text-truncate" style={{ maxWidth: '150px' }}>
                                {selectedReport.reportFriendly}
                            </h6>
                        </div>
                    </div>
                )}

                {/* Rest of navbar items */}
                <ul className="navbar-nav flex-row align-items-center ms-auto">

                    {/* If this is a Developer - provide a WP Admin icon */}
                    {user.userType === 'developer' && (
                        <a
                            href="#"
                            className="nav-link btn btn-icon btn-text-secondary rounded-pill mx-0"
                            onClick={(e) => {
                                e.preventDefault();
                            // Get token if needed for SSO
                            const idToken = localStorage.getItem('idToken');

                            // set the url and open a new tab
                            let url = PARKI_WEBSITE + '/sso-login?token=' + idToken + '&redirect_to=wp-admin';
                            window.open(url, '_blank');
                        }}
                        title="WordPress Admin"
                        data-bs-toggle="tooltip"
                        data-bs-placement="bottom">
                        <i className="icon-base ti tabler-brand-wordpress icon-22px text-heading"></i>
                    </a>
                    )}

                    {/* User Switcher Icon - Add this new component */}
                    <UserSwitcherIcon user={user} sidebarCollapsed={sidebarCollapsed} />

                    {/* Parki Settings Dropdown */}
                    <ParkiSettingsDropdown user={user} />

                    {/* User dropdown */}
                    <li className="nav-item navbar-dropdown dropdown-user dropdown">
                        <a
                            className="nav-link dropdown-toggle hide-arrow d-flex align-items-center"
                            href="#"
                            onClick={handleLinkClick}
                            data-bs-toggle="dropdown">
                            <UserAvatar bgColor="#02a5e2" isOnline={true} />
                            <div className="ms-2 d-none d-md-block">
                            <span className="fw-semibold d-block">{getUserDisplayName()}</span>
                            </div>
                        </a>
                        <ul className="dropdown-menu dropdown-menu-end pb-4">
                            {/* User dropdown menu content */}
                            <li>
                                <div className="dropdown-item mt-0" to="/settings/account">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0 me-2">
                                            <UserAvatar bgColor="#02a5e2" /> 
                                        </div>
                                        <div className="flex-grow-1">
                                            <h6 className="mb-0">{getUserDisplayName()}</h6>
                                            <small className="text-body-secondary">{user.entityName}</small>
                                        </div>
                                    </div>
                                </div>
                            </li>
                            <li>
                                <div className="dropdown-divider my-1 mx-n2"></div>
                            </li>
                            <li>
                                <Link className="dropdown-item" to="/profile">
                                    <i className="icon-base ti tabler-user me-3 icon-md"></i>
                                    <span className="align-middle">My Profile</span>
                                </Link>
                            </li>
                            <li>
                                <div className="d-grid px-2 pt-2 pb-1">
                                    <a 
                                        href="#" 
                                        onClick={handleLogout} 
                                        className="btn btn-sm btn-danger d-flex">
                                        <small className="align-middle">Logout</small>
                                        <i className="icon-base ti tabler-logout ms-2 icon-14px"></i>
                                    </a>
                                </div>
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
        </nav>
    );
};

export default Navbar;